import type React from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  useTheme,
  type InputProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useState } from 'react';

import { useTranslation } from 'react-i18next';

interface GenericAutoCompleteProps<T> {
  value: T;
  onChange: (value: T | null) => void;
  onSearch?: (searchTerm: string) => void;
  options: T[];
  equalityTest: (option: T, value: T) => boolean;
  getOptionLabel: (option: T) => string;
  placeholder?: string;
  minSearchLength?: number;
  loading?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  freeSolo?: boolean;
  defaultValue?: T | null;
  size?: 'small' | 'medium';
}

export default function GenericAutoComplete<T>({
  value,
  onChange,
  onSearch,
  options = [],
  placeholder = 'Digite o código ou nome do projeto',
  getOptionLabel,
  equalityTest,
  minSearchLength = 1,
  loading = false,
  disabled = false,
  sx = { width: '100%' },
  className,
  freeSolo = true,
  defaultValue = null,
  size = 'small',
}: GenericAutoCompleteProps<T>) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    onChange(null);
    // Trigger search if input length meets minimum requirement
    if (onSearch) {
      onSearch(newInputValue);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'end',
        ...sx,
      }}
      className={className}
    >
      <Autocomplete
        freeSolo={freeSolo}
        filterOptions={(option) => option}
        isOptionEqualToValue={equalityTest}
        sx={{ width: '100%' }}
        size={size}
        defaultValue={defaultValue}
        value={value}
        onChange={(event, newValue) => {
          onChange(newValue as T);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={inputValue.length >= 1 ? options : []}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : getOptionLabel && getOptionLabel(option)
        }
        renderOption={(params, option) => (
          <Box
            component="li"
            sx={{
              fontSize: '13px', // Using this because of incompatibility
              '& > img': { mr: 2, flexShrink: 0 },
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
          >
            {getOptionLabel(option)}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            sx={{
              '& input': {
                p: 0, // padding: 0
              },
              width: 'inherit',
              height: size === 'small' ? theme.spacing(4) : theme.spacing(5), // Using this because of incompatibility
              fontSize: '13px', // Using this because of incompatibility
            }}
            size={size}
            ref={params.InputProps.ref}
            InputProps={
              {
                ...params.inputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    {/* <img src={searchIcon || '/placeholder.svg'} alt={t('common:search.icon') as string} /> */}
                    <SearchIcon />
                  </InputAdornment>
                ),
                style: {
                  // Using this because of incompatibility
                  paddingRight: '10px',
                  paddingTop: '0px',
                  paddingBottom: '0px',
                  paddingLeft: '10px',
                  fontSize: '13px',
                },
              } as InputProps
            }
            placeholder={placeholder}
          />
        )}
      />
    </Box>
  );
}
